// Life Care Plan Page
// BP-10-04: Attorney Frontend

import { useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { ArrowLeft, Plus, FileText, Download } from 'lucide-react';
import { useCase, useLifeCarePlans, useCreateLifeCarePlan, useExportLifeCarePlanPdf } from '@/features/attorney/hooks/useAttorney';
import { LifeCarePlanEditor, CaseStatusBadge } from '../components';
import type { CaseStatus, LifeCarePlanFormData } from '@/features/attorney/types/attorney';

export function LifeCarePlanPage() {
  const { caseId } = useParams<{ caseId: string }>();
  const [selectedPlanId, setSelectedPlanId] = useState<string | null>(null);
  const [showCreateForm, setShowCreateForm] = useState(false);

  const { data: caseData } = useCase(caseId!);
  const { data: plans, isLoading } = useLifeCarePlans(caseId!);
  const createMutation = useCreateLifeCarePlan(caseId!);
  const exportMutation = useExportLifeCarePlanPdf();

  const selectedPlan = plans?.find((p) => p.id === selectedPlanId);

  const handleCreatePlan = async (data: LifeCarePlanFormData) => {
    const newPlan = await createMutation.mutateAsync(data);
    setSelectedPlanId(newPlan.id);
    setShowCreateForm(false);
  };

  const handleExportPdf = async (planId: string) => {
    const url = await exportMutation.mutateAsync(planId);
    window.open(url, '_blank');
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-start justify-between gap-4">
        <div className="flex items-start gap-4">
          <Link
            to={`/attorney/cases/${caseId}`}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors mt-1"
          >
            <ArrowLeft className="w-5 h-5 text-gray-600" />
          </Link>
          <div>
            <div className="flex items-center gap-3 mb-1">
              <h1 className="text-2xl font-semibold text-navy">Life Care Plans</h1>
              {caseData && (
                <CaseStatusBadge status={caseData.status as CaseStatus} />
              )}
            </div>
            <p className="text-gray-600">
              {caseData?.family?.name || 'Case'} • Lifetime cost projections
            </p>
          </div>
        </div>

        <button
          onClick={() => setShowCreateForm(true)}
          className="inline-flex items-center gap-2 px-4 py-2 bg-sage text-white rounded-lg hover:bg-sage-dark transition-colors"
        >
          <Plus className="w-4 h-4" />
          New Life Care Plan
        </button>
      </div>

      {/* Plan Selector (if multiple plans) */}
      {plans && plans.length > 0 && (
        <div className="flex gap-2 overflow-x-auto pb-2">
          {plans.map((plan) => (
            <button
              key={plan.id}
              onClick={() => setSelectedPlanId(plan.id)}
              className={`px-4 py-2 rounded-lg border whitespace-nowrap transition-colors ${
                selectedPlanId === plan.id
                  ? 'bg-sage text-white border-sage'
                  : 'bg-white text-gray-700 border-gray-300 hover:border-sage'
              }`}
            >
              {plan.preparerName || 'Life Care Plan'}
              <span className="ml-2 text-xs opacity-75">
                ({new Date(plan.preparationDate).toLocaleDateString()})
              </span>
            </button>
          ))}
        </div>
      )}

      {/* Life Care Plan Editor */}
      {isLoading ? (
        <div className="bg-white rounded-xl border border-gray-200 p-8 animate-pulse">
          <div className="h-8 bg-gray-200 rounded w-1/4 mb-4" />
          <div className="h-64 bg-gray-100 rounded" />
        </div>
      ) : selectedPlan ? (
        <LifeCarePlanEditor
          plan={selectedPlan}
          onExportPdf={() => handleExportPdf(selectedPlan.id)}
        />
      ) : plans && plans.length > 0 ? (
        <div className="bg-white rounded-xl border border-gray-200 p-8 text-center">
          <FileText className="w-12 h-12 mx-auto text-gray-300 mb-4" />
          <h3 className="text-lg font-medium text-navy mb-2">
            Select a Life Care Plan
          </h3>
          <p className="text-gray-500">
            Click on a plan above to view and edit details
          </p>
        </div>
      ) : (
        <div className="bg-white rounded-xl border border-gray-200 p-8 text-center">
          <FileText className="w-12 h-12 mx-auto text-gray-300 mb-4" />
          <h3 className="text-lg font-medium text-navy mb-2">
            No Life Care Plans Yet
          </h3>
          <p className="text-gray-500 mb-4">
            Create a life care plan to project lifetime costs for this case
          </p>
          <button
            onClick={() => setShowCreateForm(true)}
            className="inline-flex items-center gap-2 px-4 py-2 bg-sage text-white rounded-lg hover:bg-sage-dark transition-colors"
          >
            <Plus className="w-4 h-4" />
            Create Life Care Plan
          </button>
        </div>
      )}

      {/* Create Form Modal */}
      {showCreateForm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-xl max-h-[90vh] overflow-y-auto m-4 p-6">
            <h2 className="text-xl font-semibold text-navy mb-4">
              Create Life Care Plan
            </h2>
            <form
              onSubmit={(e) => {
                e.preventDefault();
                const formData = new FormData(e.currentTarget);
                handleCreatePlan({
                  preparerName: formData.get('preparerName') as string,
                  preparerCredentials: formData.get('preparerCredentials') as string,
                  preparerOrganization: formData.get('preparerOrganization') as string,
                  preparationDate: formData.get('preparationDate') as string,
                  lifeExpectancyYears: parseInt(formData.get('lifeExpectancyYears') as string) || 0,
                  lifeExpectancySource: formData.get('lifeExpectancySource') as string,
                  childId: caseData?.family?.children?.[0]?.id,
                });
              }}
              className="space-y-4"
            >
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Preparer Name
                </label>
                <input
                  type="text"
                  name="preparerName"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-sage/50 focus:border-sage"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Credentials
                </label>
                <input
                  type="text"
                  name="preparerCredentials"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-sage/50 focus:border-sage"
                  placeholder="e.g., RN, CLCP, CCM"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Organization
                </label>
                <input
                  type="text"
                  name="preparerOrganization"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-sage/50 focus:border-sage"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Preparation Date
                </label>
                <input
                  type="date"
                  name="preparationDate"
                  defaultValue={new Date().toISOString().split('T')[0]}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-sage/50 focus:border-sage"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Life Expectancy (Years)
                </label>
                <input
                  type="number"
                  name="lifeExpectancyYears"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-sage/50 focus:border-sage"
                  placeholder="e.g., 65"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Life Expectancy Source
                </label>
                <input
                  type="text"
                  name="lifeExpectancySource"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-sage/50 focus:border-sage"
                  placeholder="e.g., Expert opinion, actuarial tables"
                />
              </div>

              <div className="flex justify-end gap-3 pt-4">
                <button
                  type="button"
                  onClick={() => setShowCreateForm(false)}
                  className="px-4 py-2 text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={createMutation.isPending}
                  className="px-4 py-2 bg-sage text-white rounded-lg hover:bg-sage-dark transition-colors disabled:opacity-50"
                >
                  {createMutation.isPending ? 'Creating...' : 'Create Plan'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

export default LifeCarePlanPage;


